import React, { useState, useEffect } from "react";
import CountryModal from "./CountryModal";
import { motion } from "framer-motion";
import { Search, Plus, Building2, Globe } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import {
  GetCountryClausesList,
  CreateCountryClauses,
  UpdateCountryClauses,
} from "@/redux/Masters/countryclause/actions";

import { callLoader } from "@/redux/Loader/actions";
import { validateForm } from "../ui/useFormValidation";
import { showToast } from "@/redux/Toast/actions";
import { getCountryClauseColumns } from "./helper";
import { DataTable } from "../ui/table";
import Pagination from "@/pages/PortPlace/Pagination";
import { set } from "date-fns";

const MaintainCountry = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const dispatch = useDispatch();
  const [recordsPerPage, setRecordPerPage] = useState(10);
  const authUser = JSON.parse(localStorage.getItem("authUser"))?.[0];

  const initialForm = {
    id: 0,
    sName: "",
    createdDate: null,
    createdBy: "",
    modifiedDate: null,
    modifiedBy: "",
  };

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const [searchText, setSearchText] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");

  //const { commodityList, pagination } = useSelector(state => state.commodity  );
  const { countryclauseList, pagination } = useSelector(
    (state) => state.countryClause,
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
   setErrors((prev) => ({
     ...prev,
     [name]: "",
   }));
  };

  const handleDateError = (fieldName, hasError) => {};

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value, // YYYY-MM-DD
    }));
  };

  useEffect(() => {
    _getList(1);
  }, [searchText, recordsPerPage, selectedLetter]);

  const handlePrevious = () => {
    if (currentPage > 1) _getList(currentPage - 1);
  };

  const handleNext = () => {
    if (pagination?.nextPage > 0) _getList(pagination.nextPage);
  };
 const handleGoto = (page) => {
    _getList(page);
  };
  const _getList = async (page = 1) => {
    setCurrentPage(page);
    dispatch(callLoader(1)); // optional loader

    try {
      const payload = {
        IPageNo: page,
        IPageRecords: recordsPerPage,
        SearchKeyWord: searchText,
        CompanyID: authUser?.companyId,
      };

      await dispatch(GetCountryClausesList(payload));
    } catch (error) {
      // console.error("Error fetching country clauses list:", error);
      dispatch(showToast("Error fetching country clauses list", "error"));
      // optional: show toast / alert
    } finally {
      dispatch(callLoader(0));
    }
  };

  const countryValidationConfig = {
    countryClauseId: { required: true, label: "Region" },
    country: { required: true, label: "Country Name" },
    countryCode: { required: true, label: "Country ISO Code" },
    countryClause: { required: true, label: "Country Clause" },
  };

  //-- Form Click Events

  const handleAddNew = () => {
    setFormData({
      ...initialForm,
    });
    setOpenModal(true);
  };

  // Save Handler
  const handleSaveCommodity = async () => {
    const validationErrors = validateForm({
      data: formData,
      rules: countryValidationConfig,
    });

    setErrors(validationErrors || {});

    if (Object.keys(validationErrors).length > 0) {
      dispatch(showToast("Please fix the validation errors", "error"));
      return;
    }

    try {
      dispatch(callLoader(1));

      const payload = {
        totalRows: 0,
        rowNum: 0,
        id: 0,
        sName: formData.countryCode || "",
        country: formData.country || "",
        countryCode: formData.countryCode || "",
        active: true,
        countryClause: formData.countryClause || "",
        countryWarning: Boolean(formData.countryWarning),
        createdDate: null,
        createdBy: "", // authUser?.id || "",
        modifiedDate: null,
        modifiedBy: "", // authUser?.id || "",
        regionId: formData.countryClauseId || null,
        isDeleted: false,
      };

      if (Number(formData.id) === 0) {
        const addDetails = await CreateCountryClauses(payload);
        if ([200, 201].includes(addDetails?.statuscode)) {
          dispatch(showToast("Record Save successfully.", "success", true));
        }
      } else {
        const updateDetails = await UpdateCountryClauses(payload);
        if ([200, 201].includes(updateDetails?.statuscode)) {
          dispatch(showToast("Record update successfully.", "success", true));
        }
      }

      setTimeout(() => {
        setOpenModal(false);
      }, 500);
      _getList(currentPage);
    } catch (err) {
      //toast.error("Failed to save record");
      dispatch(showToast("Failed to save record", "error"));
    } finally {
      dispatch(callLoader(0));
    }
  };

  // Edit Detail
  const handleEdit = (row) => {
    setFormData({
      ...initialForm,
      ...row,
      id: Number(row.id),
      countryWarning: Boolean(row.countryWarning),
      active: Boolean(row.active),
      countryClauseId: Number(row.regionId) || 0,
      countryCode: row.countryCode || "",
      country: row.country || "",
      countryClause: row.countryClause || "",
    });

    setOpenModal(true);
  };

  const handlers = {
    handleEdit,
  };

  const columns = getCountryClauseColumns(handlers);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6"
    >
      <div className="h-full flex flex-col">
        {/* Header */}

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
                Maintain Country Clause
              </h1>
              {/* <p className="text-sm text-slate-500 dark:text-slate-400">Manage country-specific trade clauses and regulations</p> */}
            </div>
          </div>
          <button
            onClick={() => handleAddNew()}
            data-testid="add-country-clause-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Country Clause
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Enter text for search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
            />

            {/* <button
              className="px-6 py-2 bg-[#0072FF] hover:bg-[#0062DD] text-white rounded-lg font-medium transition-all"
              onClick={() => _getList(1)}
            >
              Search
            </button> */}
          </div>
        </div>

        {/* Table */}

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          <DataTable
            data={countryclauseList || []}
            columns={columns}
            trClass="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30"
            thClass="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider"
            tdClass="px-4 py-4 text-sm text-slate-600 dark:text-slate-400"
            htrClass="bg-gradient-to-r from-slate-700 to-slate-800 text-white"
            loading={false}
            Icon={Building2}
            emptyText="No Country Clause found"
            isAplyingFilter={!!searchText}
            resetFilters={() => {
              setSearchText("");
              setSelectedLetter("ALL");
            }}
          />
        </div>

        {/* Pagination */}
        {countryclauseList.length > 0 && (
          <Pagination
            currentPage={currentPage}
            nextPage={pagination?.nextPage}
            recordsPerPage={recordsPerPage}
            setRecordsPerPage={setRecordPerPage}
            onPrevious={handlePrevious}
            onNext={handleNext}
              totalRecord={pagination?.totalRecord}
              handleGoto={handleGoto}
          />
        )}
      </div>
      {/* ✅ Modal (CORRECT PLACE) */}
      <CountryModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedVessel(null);
          setFormData(initialForm);
          setErrors({});
        }}
        formData={formData}
        errors={errors}
        handleInputChange={handleInputChange}
        handleDateError={handleDateError}
        handleDateChange={handleDateChange}
        onSave={handleSaveCommodity}
        editData={selectedVessel}
      />
    </motion.div>
  );
};

export default MaintainCountry;
